import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Input, SettingsRow } from '@treams/treams-ui';

export default function LearningSubjectEdit(props: {
	learningSubject?: graphql.LearningSubject;
	onRefresh: () => void;
}) {
	const { language } = useLocalize();
	const formik = useFormik({
		initialValues: {
			translations: props.learningSubject?.title ?? [
				{
					languageID: language.id,
					text: '',
				},
			],
		},
		onSubmit: () => {},
	});

	const [editing, setEditing] = useState(!props.learningSubject);

	const isArchived = !!props.learningSubject?.isArchived;

	const [error, attempt, reset] = useErrorBoundary();

	const [update, { loading: loadingUpdate }] = graphql.useUpdateLearningSubjectMutation();
	const [create, { loading: loadingCreate }] = graphql.useCreateLearningSubjectMutation();
	const [archive, { loading: loadingArchive }] = graphql.useArchiveLearningSubjectMutation();
	const [restore, { loading: loadingRestore }] = graphql.useRestoreLearningSubjectMutation();

	const onEdit = () => {
		setEditing(true);
		reset();
	};

	const onSave = attempt(async () => {
		setEditing(false);

		const title = formik.values.translations
			.filter((translation) => translation.text.length > 0)
			.map(({ languageID, text }) => ({ languageID, text }));

		formik.values.translations = title;

		if (props.learningSubject) {
			await update({
				variables: {
					input: {
						id: props.learningSubject.id,
						title,
					},
				},
			});
		} else {
			await create({ variables: { input: { title } } });
		}

		props.onRefresh();
	});

	const onRestore = attempt(async () => {
		await restore({
			variables: {
				input: {
					id: props.learningSubject!.id,
				},
			},
		});

		props.onRefresh();
	});

	const onDelete = attempt(async () => {
		await archive({
			variables: {
				input: {
					id: props.learningSubject!.id,
				},
			},
		});

		props.onRefresh();
	});

	return (
		<SettingsRow
			loading={loadingUpdate || loadingCreate || loadingArchive || loadingRestore}
			onEdit={isArchived || editing ? undefined : onEdit}
			onRestore={isArchived ? onRestore : undefined}
			onSave={editing ? onSave : undefined}
			onDelete={props.learningSubject && !editing && !isArchived ? onDelete : undefined}
		>
			<GraphQLErrorMessage error={error} />
			{editing ? (
				<FormikProvider value={formik}>
					<Input.Localizable name="translations" />
				</FormikProvider>
			) : (
				<div>
					{
						formik.values.translations
							.filter((translation) => translation.languageID === language.id)
							.concat(formik.values.translations)[0]?.text
					}
				</div>
			)}
		</SettingsRow>
	);
}
